import { Box, Chip, Grid, Typography } from '@mui/material';
import React from 'react';
import { ReferralInsurance } from '../../../interfaces/referral';
import FieldWrapper from './FieldWrapper';

/**
 * The coverage a referral document stated, beyond the four fields that propagate.
 *
 * Read-only on purpose. These are a record of what the sender said — a payer's NAIC code, a
 * subscriber who is not the patient, a coverage window — and billing reads them to avoid
 * opening the source PDF. Making them editable belongs with the per-field editability
 * template (Workstream C), which decides who may change what; until then, showing them
 * without a way to overwrite them by accident is the safer half.
 */
const FIELDS: Array<{ key: keyof ReferralInsurance; label: string }> = [
  { key: 'payerName', label: 'Payer' },
  { key: 'payerId', label: 'Payer ID (NAIC)' },
  { key: 'planName', label: 'Plan' },
  { key: 'planType', label: 'Plan type' },
  { key: 'memberId', label: 'Member ID' },
  { key: 'subscriberId', label: 'Subscriber ID' },
  { key: 'groupId', label: 'Group ID' },
  { key: 'groupName', label: 'Group name' },
  { key: 'subscriberName', label: 'Subscriber' },
  { key: 'subscriberDateOfBirth', label: 'Subscriber DOB' },
  { key: 'subscriberAddress', label: 'Subscriber address' },
  { key: 'relationshipToSubscriber', label: 'Relationship' },
  { key: 'coverageStart', label: 'Coverage start' },
  { key: 'coverageEnd', label: 'Coverage end' },
];

interface CoverageDetailProps {
  insurance: ReferralInsurance;
}

const CoverageDetail: React.FC<CoverageDetailProps> = ({ insurance }) => {
  const present = FIELDS.filter(({ key }) => {
    const value = insurance[key];
    return typeof value === 'string' && value.trim().length > 0;
  });

  // A referral parsed from a fax carries none of this. An empty panel would read as missing
  // data rather than as a document that never stated any.
  if (present.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
          COVERAGE DETAIL
        </Typography>
        <Chip label='from the referral document' size='small' variant='outlined' />
      </Box>
      <Grid container spacing={1.5}>
        {present.map(({ key, label }) => (
          <FieldWrapper
            key={key}
            label={label}
            isEditing={false}
            readValue={insurance[key] as string}
            gridSize={{ xs: 6, md: 3 }}
          />
        ))}
      </Grid>
    </Box>
  );
};

export default CoverageDetail;
